import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import * as THREE from "three";
import {
  createViewer,
  disposeObject,
  frameObject,
  type ViewerContext,
  type ViewSide,
} from "./sceneSetup";
import { applyCanvasToMaterial, loadGLB } from "./modelLoader";
import type { WearableConfig } from "../types";

export interface ViewerHandle {
  /** Call after the texture canvas has been redrawn. */
  markTextureDirty: () => void;
  /** Return the current root Object3D so the caller can GLB-export it. */
  getRoot: () => THREE.Object3D | null;
  /** Whether the expected material was found on the current model. */
  hasMaterial: () => boolean;
  /** Snap the camera to a fixed orthogonal view of the model. */
  snapToView: (side: ViewSide) => void;
}

interface ViewerProps {
  config: WearableConfig;
  textureCanvas: HTMLCanvasElement;
  emissiveCanvas: HTMLCanvasElement;
  onStatus?: (status: ViewerStatus) => void;
}

export type ViewerStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "missing-material"; materialName: string }
  | { kind: "error"; message: string };

export const Viewer = forwardRef<ViewerHandle, ViewerProps>(function Viewer(
  { config, textureCanvas, emissiveCanvas, onStatus },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<ViewerContext | null>(null);
  const rootRef = useRef<THREE.Object3D | null>(null);
  const textureRef = useRef<THREE.CanvasTexture | null>(null);
  const emissiveTextureRef = useRef<THREE.CanvasTexture | null>(null);
  const [status, setStatus] = useState<ViewerStatus>({ kind: "idle" });

  // Report status upstream.
  useEffect(() => {
    onStatus?.(status);
  }, [status, onStatus]);

  // Imperative handle for parent.
  useImperativeHandle(
    ref,
    () => ({
      markTextureDirty: () => {
        if (textureRef.current) textureRef.current.needsUpdate = true;
        if (emissiveTextureRef.current)
          emissiveTextureRef.current.needsUpdate = true;
      },
      getRoot: () => rootRef.current,
      hasMaterial: () => textureRef.current !== null,
      snapToView: (side) => {
        const v = viewerRef.current;
        const root = rootRef.current;
        if (!v || !root) return;
        frameObject(root, v.camera, v.controls, 1.4, side);
      },
    }),
    [],
  );

  // Initialize viewer on mount.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const v = createViewer(el);
    viewerRef.current = v;
    return () => {
      v.dispose();
      viewerRef.current = null;
    };
  }, []);

  // Load/replace model when `config` changes.
  useEffect(() => {
    const v = viewerRef.current;
    if (!v) return;
    let cancelled = false;

    setStatus({ kind: "loading" });

    // Remove existing root first.
    const prev = rootRef.current;
    if (prev) {
      v.scene.remove(prev);
      disposeObject(prev);
      rootRef.current = null;
    }
    if (textureRef.current) {
      textureRef.current.dispose();
      textureRef.current = null;
    }
    if (emissiveTextureRef.current) {
      emissiveTextureRef.current.dispose();
      emissiveTextureRef.current = null;
    }

    loadGLB(config.modelPath)
      .then((gltf) => {
        if (cancelled) {
          disposeObject(gltf.scene);
          return;
        }
        const root = gltf.scene;
        v.scene.add(root);
        rootRef.current = root;

        // Ensure every node's matrixWorld is up to date before we measure
        // bounds and before we apply or export textures. Skinned meshes
        // load with bones whose world matrices must be computed at least
        // once for correct rendering.
        root.updateMatrixWorld(true);

        // Walk once to prime SkinnedMesh skeletons (bone world matrices)
        // and to log a diagnostic summary.
        let meshCount = 0;
        let skinnedCount = 0;
        root.traverse((obj) => {
          const mesh = obj as THREE.Mesh;
          if (mesh.isMesh) {
            meshCount++;
            const sm = obj as unknown as THREE.SkinnedMesh;
            if (sm.isSkinnedMesh && sm.skeleton) {
              skinnedCount++;
              sm.skeleton.update();
            }
          }
        });
        // eslint-disable-next-line no-console
        console.info(
          `[viewer] loaded ${config.modelPath}: meshes=${meshCount}, skinned=${skinnedCount}`,
        );

        const applied = applyCanvasToMaterial(
          root,
          config.materialName,
          textureCanvas,
          emissiveCanvas,
        );
        if (applied) {
          textureRef.current = applied.texture;
          emissiveTextureRef.current = applied.emissiveTexture;
          setStatus({ kind: "ready" });
        } else {
          textureRef.current = null;
          emissiveTextureRef.current = null;
          setStatus({
            kind: "missing-material",
            materialName: config.materialName,
          });
        }

        frameObject(root, v.camera, v.controls);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus({
          kind: "error",
          message:
            err instanceof Error
              ? err.message
              : `Failed to load ${config.modelPath}`,
        });
      });

    return () => {
      cancelled = true;
    };
    // Only reload when the wearable config actually changes.
    // textureCanvas identity is stable across renders from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.id, config.modelPath, config.materialName]);

  return (
    <div className="viewer-root">
      <div ref={containerRef} className="viewer-canvas" />
      {status.kind !== "ready" && (
        <div className="viewer-overlay">
          {status.kind === "loading" && <span>Loading model…</span>}
          {status.kind === "missing-material" && (
            <span className="warn">
              Material <code>{status.materialName}</code> not found in GLB.
              Texture cannot be applied.
            </span>
          )}
          {status.kind === "error" && (
            <span className="error">Error: {status.message}</span>
          )}
          {status.kind === "idle" && <span>Initializing…</span>}
        </div>
      )}
    </div>
  );
});
